/**
 * message-render-api — SERVER-SIDE render for the composer preview
 * (CC 2026-09-03 §3, JAI-1384).
 *
 *   POST /v1/admin/messages/{messageId}/render   { locale?, sample? }
 *     → { data: { messageId, locale, source: "blocks" | "html" | "none",
 *                 subject, html, text, warnings[] } }
 *
 * Why the platform renders and we do not: the preview and the send must be the
 * SAME renderer, or the preview is a second implementation that agrees with the
 * send until it doesn't. `renderEmailHtml` stays for the one case the op cannot
 * serve — a message that has never been saved has no `messageId` to render.
 *
 * `{first_name}`-style client tokens are NOT resolved by the platform, so the
 * composer keeps its own sample resolution for those (`resolveSamples`).
 *
 * Filed through the typed client's choke point (`apiRaw`) rather than a typed op
 * because the op is still in PR on their side; it moves to the generated client
 * the day it is barrelled, with no call-site change beyond this file.
 */
import { apiRaw } from "@v0y4ge/api-client";
import { reportOpSource } from "../../shell/opSource";

/** One dropped/adjusted field, named. Never a 400 — see `warnings[]` in §3. */
export interface MsgWarning { code: string; field?: string; message: string }

export interface MsgRender {
  messageId: string;
  locale: string;
  /** `blocks` = rendered from the block document · `html` = code mode ·
   *  `none` = the message has no email content to render. */
  source: "blocks" | "html" | "none";
  subject: string;
  html: string;
  text: string;
  warnings: MsgWarning[];
}

export function renderMessage(messageId: string, opts: { locale?: string; sample?: Record<string, string> } = {}): Promise<MsgRender> {
  return apiRaw<{ data?: MsgRender } & Partial<MsgRender>>(
    `/v1/admin/messages/${encodeURIComponent(messageId)}/render`,
    { method: "POST", body: JSON.stringify({ ...(opts.locale ? { locale: opts.locale } : {}), ...(opts.sample ? { sample: opts.sample } : {}) }) },
    "renderMessage",
  ).then((r) => {
    reportOpSource("renderMessage", r);
    const d = (r?.data ?? r) as MsgRender;
    return { ...d, warnings: d?.warnings ?? [] };
  });
}

/** Warnings off any create/update/render response, whatever the envelope. */
export function warningsOf(res: unknown): MsgWarning[] {
  const r = res as { warnings?: MsgWarning[]; data?: { warnings?: MsgWarning[] } } | null | undefined;
  return r?.warnings ?? r?.data?.warnings ?? [];
}
